import { useState, useEffect, useContext } from 'react';
import { BallTriangle } from 'react-loader-spinner';
import toast from 'react-hot-toast';
import Nodatafound from 'pages/Common/Nodatafound';
import InputField from 'components/fields/InputField';
import Card from 'components/card';
import studentService from 'ApiService/Admin/studentService';
import { formatDate } from 'pages/helpers/dateUtils';
import { AuthContext } from 'contexts/AuthContext';
import { MdChevronRight, MdKeyboardArrowDown, MdClose, MdVisibility } from 'react-icons/md';

const ExternalMockResultsView = ({ classData, handleBack }) => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [mockResults, setMockResults] = useState([]);
  const [expandedEmails, setExpandedEmails] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [viewAllStudent, setViewAllStudent] = useState(null);
  const { state } = useContext(AuthContext);
  const selectedBranch = state?.selectedBranch || '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await studentService.getExternalMockResults(
          classData.courseId,
          classData.batchId,
          selectedBranch
        );
        if (response.data.success) {
          setStudents(response.data.students);
          setMockResults(response.data.mockResults);
        } else {
          toast.error('Failed to fetch mock results');
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to fetch mock results');
      } finally {
        setLoading(false);
      }
    };
    if (classData.courseId && classData.batchId) {
      fetchData();
    }
  }, [classData, selectedBranch]);

  const mockByEmail = {};
  mockResults.forEach((r) => {
    const email = r.studentEmail?.toLowerCase().trim();
    if (!mockByEmail[email]) mockByEmail[email] = [];
    mockByEmail[email].push(r);
  });

  const subjects = [...new Set(mockResults.map((r) => r.subjectName).filter(Boolean))];

  const getStudentResults = (email) => mockByEmail[email?.toLowerCase().trim()] || [];

  const allStudentData = students.map((s) => {
    const results = getStudentResults(s.email);
    const filtered = selectedSubject
      ? results.filter((r) => r.subjectName === selectedSubject)
      : results;
    return { ...s, results: filtered, allResults: results };
  });

  const filteredStudents = allStudentData.filter((s) => {
    const matchSearch = s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchSearch;
  });

  const toggleExpand = (email) => {
    setExpandedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const calcStudentStats = (results) => {
    if (!results.length) return null;
    const marksList = results.filter((r) => r.Marks != null && r.TotalMarks != null);
    if (!marksList.length) return null;
    const avg = marksList.reduce((sum, r) => sum + (r.Marks / r.TotalMarks) * 100, 0) / marksList.length;
    const passCount = marksList.filter((r) => (r.Marks / r.TotalMarks) >= 0.35).length;
    return { totalTests: marksList.length, avg: parseFloat(avg.toFixed(1)), passCount, passRate: parseFloat(((passCount / marksList.length) * 100).toFixed(1)) };
  };

  const totalAllResults = mockResults.length;
  const studentsWithData = new Set(mockResults.map((r) => r.studentEmail?.toLowerCase().trim())).size;
  const allMarks = mockResults.filter((r) => r.Marks != null && r.TotalMarks != null);
  const overallAvg = allMarks.length
    ? parseFloat((allMarks.reduce((s, r) => s + (r.Marks / r.TotalMarks) * 100, 0) / allMarks.length).toFixed(1))
    : 0;
  const overallPassCount = allMarks.filter((r) => (r.Marks / r.TotalMarks) >= 0.35).length;
  const overallPassRate = allMarks.length ? parseFloat(((overallPassCount / allMarks.length) * 100).toFixed(1)) : 0;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <BallTriangle color="#5431ed" />
      </div>
    );
  }

  return (
    <Card extra="w-full sm:overflow-auto p-4">
      <div className="mb-4 flex flex-col space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="w-full">
            <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
              Filter by Subject
            </label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            >
              <option value="">All Subjects</option>
              {subjects.map((sub) => (
                <option key={sub} value={sub}>{sub}</option>
              ))}
            </select>
          </div>
          <div className="w-full">
            <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
              Search Student
            </label>
            <InputField
              variant="auth"
              extra="mt-2"
              placeholder="Type name or email..."
              id="search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div className="rounded-lg bg-blue-50 p-4 dark:bg-navy-800">
            <h3 className="text-sm font-semibold">Total Tests</h3>
            <p className="text-lg font-bold">{totalAllResults}</p>
          </div>
          <div className="rounded-lg bg-green-50 p-4 dark:bg-navy-800">
            <h3 className="text-sm font-semibold">Students with Data</h3>
            <p className="text-lg font-bold">{studentsWithData}</p>
          </div>
          <div className="rounded-lg bg-purple-50 p-4 dark:bg-navy-800">
            <h3 className="text-sm font-semibold">Average Marks</h3>
            <p className="text-lg font-bold">{overallAvg}%</p>
          </div>
          <div className="rounded-lg bg-yellow-50 p-4 dark:bg-navy-800">
            <h3 className="text-sm font-semibold">Pass Rate</h3>
            <p className="text-lg font-bold">{overallPassRate}%</p>
          </div>
        </div>

        {!students.length && !loading && (
          <div className="text-center text-gray-600 dark:text-gray-400">
            <Nodatafound title="No students found for the selected batch." />
          </div>
        )}

        {students.length > 0 && filteredStudents.length === 0 && (
          <div className="text-center text-gray-600 dark:text-gray-400">
            <Nodatafound title={`No students matching "${searchQuery}"`} />
          </div>
        )}

        {filteredStudents.length > 0 && (
          <div className="mt-4 space-y-2">
            {filteredStudents.map((student) => {
              const stats = calcStudentStats(selectedSubject ? student.results : student.allResults);
              const hasResults = student.allResults.length > 0;
              const displayResults = selectedSubject ? student.results : student.allResults;
              const isExpanded = expandedEmails.has(student.email);

              return (
                <div key={student._id} className="rounded-lg border border-gray-200 dark:border-white/10">
                  <button
                    onClick={() => hasResults && toggleExpand(student.email)}
                    className={`flex w-full items-center justify-between px-4 py-3 text-left transition hover:bg-gray-50 dark:hover:bg-navy-700 ${hasResults ? 'cursor-pointer' : 'cursor-default'}`}
                  >
                    <div className="flex flex-1 items-center gap-3">
                      {hasResults ? (
                        isExpanded ? <MdKeyboardArrowDown className="text-xl" /> : <MdChevronRight className="text-xl" />
                      ) : (
                        <span className="w-5" />
                      )}
                      <div className="flex-1">
                        <span className="font-medium text-navy-700 dark:text-white">
                          {student.name}
                        </span>
                        <span className="ml-3 text-sm text-gray-500">{student.email}</span>
                      </div>
                      {stats ? (
                        <div className="flex gap-4 text-sm">
                          <span className="text-blue-600">{stats.totalTests} test{stats.totalTests > 1 ? 's' : ''}</span>
                          <span className="text-green-600">Avg: {stats.avg}%</span>
                          <span className={stats.passRate >= 50 ? 'text-green-600' : 'text-red-600'}>
                            Pass: {stats.passRate}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">No mock data</span>
                      )}
                      {hasResults && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setViewAllStudent(student); }}
                          className="ml-3 flex items-center gap-1 rounded-lg bg-brand-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-800 dark:bg-brand-400 dark:hover:bg-brand-300"
                        >
                          <MdVisibility className="text-sm" />
                          View All
                        </button>
                      )}
                    </div>
                  </button>

                  {isExpanded && hasResults && (
                    <div className="overflow-x-auto border-t border-gray-200 dark:border-white/10">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 dark:bg-navy-800">
                            <th className="px-4 py-2 text-left text-xs font-bold">#</th>
                            <th className="px-4 py-2 text-left text-xs font-bold">Subject</th>
                            <th className="px-4 py-2 text-left text-xs font-bold">Date</th>
                            <th className="px-4 py-2 text-center text-xs font-bold">Marks</th>
                            <th className="px-4 py-2 text-center text-xs font-bold">%</th>
                            <th className="px-4 py-2 text-center text-xs font-bold">Correct</th>
                            <th className="px-4 py-2 text-center text-xs font-bold">Wrong</th>
                            <th className="px-4 py-2 text-left text-xs font-bold">Teacher</th>
                            <th className="px-4 py-2 text-center text-xs font-bold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayResults.map((r, idx) => {
                            const pct = r.TotalMarks ? ((r.Marks / r.TotalMarks) * 100).toFixed(1) : 'N/A';
                            const status = r.Marks != null && r.TotalMarks != null
                              ? (r.Marks / r.TotalMarks) >= 0.35 ? 'Pass' : 'Fail'
                              : r.status || 'N/A';
                            return (
                              <tr key={r._id} className="border-b border-gray-100 dark:border-white/5">
                                <td className="px-4 py-2 text-sm">{idx + 1}</td>
                                <td className="px-4 py-2 text-sm font-medium">{r.subjectName}</td>
                                <td className="px-4 py-2 text-sm">{r.testDate ? formatDate(r.testDate) : '-'}</td>
                                <td className="px-4 py-2 text-center text-sm">
                                  {r.Marks != null ? `${r.Marks}/${r.TotalMarks}` : '-'}
                                </td>
                                <td className="px-4 py-2 text-center text-sm">{pct}</td>
                                <td className="px-4 py-2 text-center text-sm text-green-600">{r.correctAnswers ?? '-'}</td>
                                <td className="px-4 py-2 text-center text-sm text-red-600">{r.wrongAnswers ?? '-'}</td>
                                <td className="px-4 py-2 text-sm">{r.teacherName || '-'}</td>
                                <td className="px-4 py-2 text-center text-sm">
                                  <span className={`rounded-full px-2 py-0.5 text-xs ${
                                    status === 'Pass'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : status === 'Fail'
                                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                  }`}>
                                    {status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-start pt-4">
          <button
            onClick={handleBack}
            className="cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20"
          >
            Back
          </button>
        </div>
      </div>

      {viewAllStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-auto rounded-2xl bg-white p-6 dark:bg-navy-800">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-navy-700 dark:text-white">{viewAllStudent.name}</h2>
                <p className="text-sm text-gray-500">{viewAllStudent.email}</p>
              </div>
              <button
                onClick={() => setViewAllStudent(null)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700"
              >
                <MdClose className="text-xl" />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-navy-700">
                    <th className="px-4 py-2 text-left text-xs font-bold">#</th>
                    <th className="px-4 py-2 text-left text-xs font-bold">Subject</th>
                    <th className="px-4 py-2 text-left text-xs font-bold">Date</th>
                    <th className="px-4 py-2 text-center text-xs font-bold">Marks</th>
                    <th className="px-4 py-2 text-center text-xs font-bold">%</th>
                    <th className="px-4 py-2 text-center text-xs font-bold">Correct</th>
                    <th className="px-4 py-2 text-center text-xs font-bold">Wrong</th>
                    <th className="px-4 py-2 text-left text-xs font-bold">Teacher</th>
                    <th className="px-4 py-2 text-center text-xs font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {getStudentResults(viewAllStudent.email).map((r, idx) => {
                    const pct = r.TotalMarks ? ((r.Marks / r.TotalMarks) * 100).toFixed(1) : 'N/A';
                    const status = r.Marks != null && r.TotalMarks != null
                      ? (r.Marks / r.TotalMarks) >= 0.35 ? 'Pass' : 'Fail'
                      : r.status || 'N/A';
                    return (
                      <tr key={r._id} className="border-b border-gray-100 dark:border-white/5">
                        <td className="px-4 py-2 text-sm">{idx + 1}</td>
                        <td className="px-4 py-2 text-sm font-medium">{r.subjectName}</td>
                        <td className="px-4 py-2 text-sm">{r.testDate ? formatDate(r.testDate) : '-'}</td>
                        <td className="px-4 py-2 text-center text-sm">
                          {r.Marks != null ? `${r.Marks}/${r.TotalMarks}` : '-'}
                        </td>
                        <td className="px-4 py-2 text-center text-sm">{pct}</td>
                        <td className="px-4 py-2 text-center text-sm text-green-600">{r.correctAnswers ?? '-'}</td>
                        <td className="px-4 py-2 text-center text-sm text-red-600">{r.wrongAnswers ?? '-'}</td>
                        <td className="px-4 py-2 text-sm">{r.teacherName || '-'}</td>
                        <td className="px-4 py-2 text-center text-sm">
                          <span className={`rounded-full px-2 py-0.5 text-xs ${
                            status === 'Pass'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : status === 'Fail'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}>
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {getStudentResults(viewAllStudent.email).length === 0 && (
                    <tr>
                      <td colSpan="9" className="py-4 text-center text-sm text-gray-400">No mock results found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default ExternalMockResultsView;
